import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage, isApiError } from "@/services/bedrock/apiError";
import { DesignPackageItemServices } from "@/services/bedrock/DesignPackageItemServices";
import { DesignTemplateServices } from "@/services/bedrock/DesignTemplateServices";
import { ApplyTemplateHtmlRequest, TestRenderRequest } from "@/services/bedrock/dtos/DesignTemplateSource";
import { designKeys } from "@/services/bedrock/queryKeys";

const GENERIC_ERROR = "Something went wrong. Try again.";

export interface TestRenderVariables {
  templateId: number;
  body: TestRenderRequest;
}

export interface ApplyVariables {
  itemId: number;
  body: ApplyTemplateHtmlRequest;
}

export const APPLY_TOASTS = {
  sent: "New version sent to the coach",
  applied: "New version applied",
};

/**
 * The Source sheet's two writes. A test render resolves to its draft group, which the sheet
 * follows through useRenderGroup; applying refreshes everything the new version touches.
 */
export function useSourceMutations(pkgId: number) {
  const queryClient = useQueryClient();

  const testRender = useMutation({
    mutationFn: ({ templateId, body }: TestRenderVariables) => DesignTemplateServices.testRender(templateId, body),
    onError: (error: unknown) => toast.error(apiErrorMessage(error, GENERIC_ERROR)),
  });

  const apply = useMutation({
    mutationFn: ({ itemId, body }: ApplyVariables) => DesignPackageItemServices.createTemplate(itemId, body),
    onSuccess: (_item, { body }) => {
      queryClient.invalidateQueries({ queryKey: designKeys.detail(pkgId) });
      queryClient.invalidateQueries({ queryKey: designKeys.activity(pkgId) });
      queryClient.invalidateQueries({ queryKey: designKeys.previews(pkgId, null).slice(0, 2) });
      queryClient.invalidateQueries({ queryKey: designKeys.trace(pkgId) });
      toast.success(body.approve ? APPLY_TOASTS.applied : APPLY_TOASTS.sent);
    },
    onError: (error: unknown) => {
      if (isApiError(error, "refine_in_progress")) toast.error("Wait for the current change to finish.");
      else toast.error(apiErrorMessage(error, GENERIC_ERROR));
    },
  });

  return { testRender, apply };
}
